import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  CLASSIFICATION_COLORS,
  FETCH_CATEGORY_CARD_DATA,
} from "../../DataTagConstants";
import "./CategoryRadarChart.scss";
import { numberFormatter } from "../../../../components/common/Dashboard/utils";

const FIXED_CATEGORIES = [
  "PERSONAL",
  "MARKETING",
  "FINANCIAL",
  "ORGANIZATIONAL",
  "MEDICAL",
  "API",
  "NO_SENSITIVITY",
  "OTHER",
];

const categoryNames = {
  PERSONAL: "Personal",
  MARKETING: "Marketing",
  FINANCIAL: "Financial",
  ORGANIZATIONAL: "Organizational",
  MEDICAL: "Medical",
  API: "API",
  NO_SENSITIVITY: "No Sensitivity",
  OTHER: "Other",
};

// Tick wrapper for long labels
const WrappedTick = ({ payload, x, y, textAnchor }) => {
  const words = payload.value.split(" ");
  return (
    <text x={x} y={y} textAnchor={textAnchor} fill="#444" fontSize={10}>
      {words.map((word, index) => (
        <tspan key={index} x={x} dy={index === 0 ? 0 : 10}>
          {word}
        </tspan>
      ))}
    </text>
  );
};

// Tooltip showing the REAL value (not log)
const SingleValueTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;

  const item = payload[0];

  return (
    <div className="custom-tooltip-data-tag">
      <div className="tooltip-label-data-tag">{item.payload.category}</div>
      <div className="tooltip-value-data-tag">{numberFormatter(item.payload.value)}</div>
    </div>
  );
};

export default function CategoryRadarChart() {
  const dispatch = useDispatch();
  const dataSourceId = useSelector(
    (state) => state.dataTagReducer.selectedDataSource?.id
  );
  const rawData = useSelector((state) => state.dataTagReducer.categoryCardData);

  useEffect(() => {
    if (dataSourceId) {
      dispatch({ type: FETCH_CATEGORY_CARD_DATA, payload: dataSourceId });
    }
  }, [dataSourceId]);

  // Build full dataset + LOG SCALE
  const fullData = useMemo(() => {
    const mapped = FIXED_CATEGORIES.map((cat) => {
      const found = rawData?.find(
        (item) => item["DataTagSensitivity.finalType"] === cat
      );

      const value = found ? Number(found["DataTagSensitivity.count"]) : 0;
      const logValue = value > 0 ? Math.log10(value + 1) : 0.2;

      return {
        category: categoryNames[cat],
        value,
        logValue,
        fill: CLASSIFICATION_COLORS[cat],
      };
    });

    const nonZero = mapped.filter((d) => d.value > 0);
    const zeroes = mapped.filter((d) => d.value === 0);

    return [...nonZero, ...zeroes];
  }, [rawData]);

  // Max of log values
  const maxLog = Math.max(...fullData.map((d) => d.logValue), 1);

  return (
    <div className="radar-chart-wrapper">
      <ResponsiveContainer width="100%" height={200}>
        <RadarChart data={fullData}>
          <PolarGrid />

          <PolarAngleAxis dataKey="category" tick={<WrappedTick />} />

          <PolarRadiusAxis
            tick={false}
            axisLine={false}
            domain={[0, maxLog]}
            angle={30}
          />

          <Radar
            name="Categories"
            dataKey="logValue"
            stroke="#3f51b5"
            fill="#3f51b5"
            fillOpacity={0.45}
          />

          <Tooltip content={<SingleValueTooltip />} />
        </RadarChart>
      </ResponsiveContainer>
    </div>
  );
}
